import React, { useEffect, useId, useRef, useState } from 'react';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { onStickyOffsetChange, readStickyOffset } from '../../utils/stickyOffset';
import './SecondaryNav.scss';

const MAX_LINKS = 6;
const MAX_DROPDOWN_ITEMS = 12;

const isSafeHref = (href: string | undefined): string | undefined =>
  href && /^(https?:\/\/|#|\/)/i.test(href.trim()) ? href : undefined;

type SecondaryNavItem =
  | { kind: 'link'; text: string; href: string | undefined; index: number }
  | { kind: 'dropdown' };

/**
 * SecondaryNav - slim frosted-glass contextual navigation bar.
 *
 * Renders a service/section title on the left plus a right-aligned row of up
 * to six in-page nav links, with a single optional "dropdown" nav item (e.g.
 * "Explore services") that opens a flyout panel of secondary links. When
 * present, the dropdown always sits at the right-hand end of the row.
 * The bar pins to the top of the viewport once the user scrolls past it
 * (sticky-on-scroll), detected via an IntersectionObserver on a zero-height
 * sentinel — the same pattern used by MegaMenu. Below the tablet breakpoint
 * the right-side items collapse into a disclosure dropdown toggled by the
 * chevron beside the title.
 *
 * Content is supplied through flat, CMS-friendly props (no arrays/objects) to
 * satisfy the Polyphemus authoring constraint.
 *
 * @polySemanticUsage About this block: a contextual sub-navigation element that sits below the
 * header or within a page section. Use it to provide wayfinding within a content hub, category,
 * or multi-step flow (e.g. tabbed navigation, anchor links, or breadcrumb-style trails).
 * Migration rule: do NOT use this block during automated migration. Source sidecar
 * navigation — clickable navigation links rendered as their own distinct component alongside
 * the page content (e.g. a side rail or column of section/page links) — is not migrated: omit
 * it from the migration output entirely and do not repurpose its links into any other block.
 * This covers only navigation that is a separate component beside the content — not tab rows,
 * breadcrumbs, anchor-link strips, or links inline within content sections. Note the omission
 * in your migration self-evaluation notes only.
 */
export interface SecondaryNavProps {
  // Every href here stays Text rather than @polyType Url: PolyContentService's Url validation
  // rejects anchor-only values such as '#faq', and in-page anchors are this block's primary use.
  /**
   * Left-aligned service or section title.
   * @polyLabel Title
   * @polyTranslatable true
   */
  titleText?: string;

  /**
   * Optional destination for the title: an in-page anchor (#section), a site path (/page)
   * or a full URL. When set, the title renders as a link.
   * @polyTranslatable false
   */
  titleHref?: string;
  /**
   * @polyLabel Link 1 Text
   * @polyTranslatable true
   */
  link1Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  link1Href?: string;

  /**
   * @polyLabel Link 2 Text
   * @polyTranslatable true
   */
  link2Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  link2Href?: string;

  /**
   * @polyLabel Link 3 Text
   * @polyTranslatable true
   */
  link3Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  link3Href?: string;

  /**
   * @polyLabel Link 4 Text
   * @polyTranslatable true
   */
  link4Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  link4Href?: string;

  /**
   * @polyLabel Link 5 Text
   * @polyTranslatable true
   */
  link5Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  link5Href?: string;

  /**
   * @polyLabel Link 6 Text
   * @polyTranslatable true
   */
  link6Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  link6Href?: string;

  /**
   * 1-based index (1–6) of the link to mark as the current/active item.
   * @polyType Text
   * @polyTranslatable false
   */
  activeLinkIndex?: number;

  /**
   * Dropdown nav item label (e.g. "Explore services"). When set alongside at
   * least one dropdown item, a flyout menu is rendered after the links, at the
   * right-hand end of the bar.
   * @polyLabel Dropdown Label
   * @polyTranslatable true
   */
  dropdownText?: string;

  /**
   * @polyLabel Dropdown Item 1 Text
   * @polyTranslatable true
   */
  dropdownItem1Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  dropdownItem1Href?: string;
  /**
   * @polyLabel Dropdown Item 2 Text
   * @polyTranslatable true
   */
  dropdownItem2Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  dropdownItem2Href?: string;
  /**
   * @polyLabel Dropdown Item 3 Text
   * @polyTranslatable true
   */
  dropdownItem3Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  dropdownItem3Href?: string;
  /**
   * @polyLabel Dropdown Item 4 Text
   * @polyTranslatable true
   */
  dropdownItem4Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  dropdownItem4Href?: string;
  /**
   * @polyLabel Dropdown Item 5 Text
   * @polyTranslatable true
   */
  dropdownItem5Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  dropdownItem5Href?: string;
  /**
   * @polyLabel Dropdown Item 6 Text
   * @polyTranslatable true
   */
  dropdownItem6Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  dropdownItem6Href?: string;
  /**
   * @polyLabel Dropdown Item 7 Text
   * @polyTranslatable true
   */
  dropdownItem7Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  dropdownItem7Href?: string;
  /**
   * @polyLabel Dropdown Item 8 Text
   * @polyTranslatable true
   */
  dropdownItem8Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  dropdownItem8Href?: string;
  /**
   * @polyLabel Dropdown Item 9 Text
   * @polyTranslatable true
   */
  dropdownItem9Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  dropdownItem9Href?: string;
  /**
   * @polyLabel Dropdown Item 10 Text
   * @polyTranslatable true
   */
  dropdownItem10Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  dropdownItem10Href?: string;
  /**
   * @polyLabel Dropdown Item 11 Text
   * @polyTranslatable true
   */
  dropdownItem11Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  dropdownItem11Href?: string;
  /**
   * @polyLabel Dropdown Item 12 Text
   * @polyTranslatable true
   */
  dropdownItem12Text?: string;
  /**
   * Link destination: an in-page anchor (#section), a site path (/page) or a full URL.
   * @polyTranslatable false
   */
  dropdownItem12Href?: string;

  /**
   * Accessible label for the nav landmark. Falls back to the title, then
   * "Secondary navigation". No `@polyDefault`: a stored default would
   * override the title fallback on every new block.
   * @polyLabel Screen Reader Label
   * @polyHelp Name screen readers announce for this navigation. Leave empty to use the title.
   * @polyTranslatable true
   */
  ariaLabel?: string;
}

export const SecondaryNav: React.FC<SecondaryNavProps> = ({
  titleText,
  titleHref,
  link1Text, link1Href,
  link2Text, link2Href,
  link3Text, link3Href,
  link4Text, link4Href,
  link5Text, link5Href,
  link6Text, link6Href,
  activeLinkIndex,
  dropdownText,
  dropdownItem1Text, dropdownItem1Href,
  dropdownItem2Text, dropdownItem2Href,
  dropdownItem3Text, dropdownItem3Href,
  dropdownItem4Text, dropdownItem4Href,
  dropdownItem5Text, dropdownItem5Href,
  dropdownItem6Text, dropdownItem6Href,
  dropdownItem7Text, dropdownItem7Href,
  dropdownItem8Text, dropdownItem8Href,
  dropdownItem9Text, dropdownItem9Href,
  dropdownItem10Text, dropdownItem10Href,
  dropdownItem11Text, dropdownItem11Href,
  dropdownItem12Text, dropdownItem12Href,
  ariaLabel,
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [stickyOffset, setStickyOffset] = useState(0);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const dropdownId = useId();

  // Pin once the sentinel above the bar scrolls under the pinned MegaMenu (or the window top
  // when nothing is pinned). The observer's top margin is the same offset the fixed bar is
  // placed at, so the bar never scrolls behind the menu before it pins. Guarded so SSR and
  // environments without IntersectionObserver no-op safely.
  useEffect(() => {
    setStickyOffset(readStickyOffset());
    return onStickyOffsetChange(setStickyOffset);
  }, []);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsSticky(!entry.isIntersecting),
      { threshold: 0, rootMargin: `${-stickyOffset}px 0px 0px 0px` }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [stickyOffset]);

  // Dismiss the mobile menu and the dropdown flyout on Escape or outside click.
  useEffect(() => {
    if (!isMenuOpen && !isDropdownOpen) return;
    const closeAll = () => {
      setIsMenuOpen(false);
      setIsDropdownOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Close the innermost open layer first (WAI-ARIA menu-button pattern):
      // the dropdown flyout, then the mobile menu on a subsequent Escape.
      // Restore focus to the control that owned the closed layer.
      if (isDropdownOpen) {
        setIsDropdownOpen(false);
        dropdownTriggerRef.current?.focus();
      } else if (isMenuOpen) {
        setIsMenuOpen(false);
        menuToggleRef.current?.focus();
      }
    };
    const handlePointerDown = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        closeAll();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isMenuOpen, isDropdownOpen]);

  const links = [
    { text: link1Text, href: link1Href },
    { text: link2Text, href: link2Href },
    { text: link3Text, href: link3Href },
    { text: link4Text, href: link4Href },
    { text: link5Text, href: link5Href },
    { text: link6Text, href: link6Href },
  ]
    .slice(0, MAX_LINKS)
    .map((link, index) => ({ ...link, index: index + 1 }))
    .filter((link): link is { text: string; href: string | undefined; index: number } => !!link.text);

  const dropdownItems = [
    { text: dropdownItem1Text, href: dropdownItem1Href },
    { text: dropdownItem2Text, href: dropdownItem2Href },
    { text: dropdownItem3Text, href: dropdownItem3Href },
    { text: dropdownItem4Text, href: dropdownItem4Href },
    { text: dropdownItem5Text, href: dropdownItem5Href },
    { text: dropdownItem6Text, href: dropdownItem6Href },
    { text: dropdownItem7Text, href: dropdownItem7Href },
    { text: dropdownItem8Text, href: dropdownItem8Href },
    { text: dropdownItem9Text, href: dropdownItem9Href },
    { text: dropdownItem10Text, href: dropdownItem10Href },
    { text: dropdownItem11Text, href: dropdownItem11Href },
    { text: dropdownItem12Text, href: dropdownItem12Href },
  ]
    .slice(0, MAX_DROPDOWN_ITEMS)
    .map((item, index) => ({ ...item, index: index + 1 }))
    .filter((item): item is { text: string; href: string | undefined; index: number } => !!item.text);

  const hasDropdown = !!(dropdownText && dropdownItems.length > 0);

  if (!titleText && links.length === 0 && !hasDropdown) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[SecondaryNav] No title, links, or dropdown provided — nothing to render.');
    }
    return null;
  }

  // The dropdown always renders last, at the right-hand end of the nav items.
  const navItems: SecondaryNavItem[] = links.map((link): SecondaryNavItem => ({ kind: 'link', ...link }));
  if (hasDropdown) {
    navItems.push({ kind: 'dropdown' });
  }

  const navClasses = [
    'poly-secondary-nav',
    isSticky ? 'poly-secondary-nav--sticky' : '',
    isMenuOpen ? 'poly-secondary-nav--menu-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const resolvedAriaLabel = ariaLabel || titleText || 'Secondary navigation';
  const hasMenu = navItems.length > 0;

  return (
    <>
      <div ref={sentinelRef} className="poly-secondary-nav__sticky-sentinel" aria-hidden="true" />
      {isSticky && <div className="poly-secondary-nav__sticky-spacer" aria-hidden="true" />}
      <nav ref={navRef} className={navClasses} aria-label={resolvedAriaLabel}>
        <div className="poly-secondary-nav__bar">
          <div className="poly-secondary-nav__title-group">
            {titleText &&
              (titleHref ? (
                <a href={isSafeHref(titleHref)} className="poly-secondary-nav__title">
                  {titleText}
                </a>
              ) : (
                <span className="poly-secondary-nav__title">{titleText}</span>
              ))}
            {hasMenu && (
              <button
                ref={menuToggleRef}
                type="button"
                className="poly-secondary-nav__toggle"
                aria-expanded={isMenuOpen}
                aria-controls={menuId}
                aria-label={titleText ? `${titleText} menu` : 'Section navigation menu'}
                onClick={() => {
                  // Collapsing the menu also resets the nested dropdown so it
                  // doesn't reappear already expanded when reopened.
                  if (isMenuOpen) setIsDropdownOpen(false);
                  setIsMenuOpen((open) => !open);
                }}
              >
                <CustomIcon
                  name="chevron-down"
                  size={24}
                  className="poly-secondary-nav__chevron"
                  aria-hidden="true"
                />
              </button>
            )}
          </div>

          {hasMenu && (
            <div id={menuId} className="poly-secondary-nav__menu">
              <ul className="poly-secondary-nav__links">
                {navItems.map((item) => {
                  if (item.kind === 'dropdown') {
                    return (
                      <li key="dropdown" className="poly-secondary-nav__item poly-secondary-nav__dropdown">
                        <button
                          ref={dropdownTriggerRef}
                          type="button"
                          className="poly-secondary-nav__dropdown-trigger"
                          aria-expanded={isDropdownOpen}
                          aria-controls={dropdownId}
                          onClick={() => setIsDropdownOpen((open) => !open)}
                        >
                          {dropdownText}
                          <CustomIcon
                            name="chevron-down"
                            size={20}
                            className="poly-secondary-nav__dropdown-chevron"
                            aria-hidden="true"
                          />
                        </button>
                        {isDropdownOpen && (
                          <ul id={dropdownId} className="poly-secondary-nav__dropdown-panel">
                            {dropdownItems.map((dropdownItem) => (
                              <li
                                key={dropdownItem.index}
                                className="poly-secondary-nav__dropdown-panel-item"
                              >
                                <a href={isSafeHref(dropdownItem.href)} className="poly-secondary-nav__dropdown-link">
                                  {dropdownItem.text}
                                </a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  }

                  const isActive = Number(activeLinkIndex) === item.index;
                  const linkClasses = [
                    'poly-secondary-nav__link',
                    isActive ? 'poly-secondary-nav__link--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <li key={`link-${item.index}`} className="poly-secondary-nav__item">
                      <a
                        href={isSafeHref(item.href)}
                        className={linkClasses}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        {item.text}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};
